import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Cpu,
  Radio,
  RefreshCw,
  Upload,
  UserPlus,
  Users,
  Wifi,
} from "lucide-react";
import {
  AdminOverview,
  getAdminOverview,
} from "../../../Services/Admin/Overview";
import "./OverviewPanel.css";

type Navigate = (
  section: "venues" | "sensors" | "users" | "recognition" | "firmware" | "imports",
  target?: string
) => void;

const number = new Intl.NumberFormat("en");

export default function OverviewPanel({ onNavigate }: { onNavigate: Navigate }) {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getAdminOverview()
      .then(setData)
      .catch((e) => setError(e?.message ?? "Could not load the operations overview"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading && !data) {
    return (
      <div className="overview-skeleton" aria-label="Loading operations overview">
        {Array.from({ length: 4 }, (_, index) => (
          <div className="admin-kpi admin-kpi--loading" key={index} />
        ))}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="admin-state-card">
        <AlertTriangle size={22} />
        <div>
          <strong>Overview unavailable</strong>
          <p>{error}. Check the API connection, then try again.</p>
        </div>
        <button type="button" onClick={load}>Try again</button>
      </div>
    );
  }

  if (!data) return null;

  const attention = [
    {
      count: data.sensors.offline,
      label: "Offline sensors",
      detail: "Sensors outside their expected heartbeat window",
      section: "sensors" as const,
    },
    {
      count: data.sensors.unclaimed,
      label: "Unclaimed sensors",
      detail: "Authenticated hardware waiting for a venue",
      section: "sensors" as const,
      target: "sensor-provisioning",
    },
    {
      count: data.sensors.failedOta,
      label: "Failed OTA updates",
      detail: "Sensors reporting a failed firmware update",
      section: "sensors" as const,
    },
    {
      count: data.venues.needsReview,
      label: "Venues need review",
      detail: "Imported venues that are still hidden from the map",
      section: "venues" as const,
    },
    {
      count: data.venues.missingHours,
      label: "Venues missing hours",
      detail: "Recognition cannot run without opening hours",
      section: "venues" as const,
    },
    {
      count: data.venues.premiumWithoutSensor,
      label: "Premium venues without sensors",
      detail: "Paying venues with no connected hardware",
      section: "venues" as const,
    },
    {
      count: data.imports.failed,
      label: "Failed imports",
      detail: "Venue import jobs requiring investigation",
      section: "imports" as const,
    },
  ].filter((item) => item.count > 0);

  const usage = [
    ["Venue views", data.productUsage.venueViews],
    ["Directions clicks", data.productUsage.directionsClicks],
    ["Check-ins", data.productUsage.checkIns],
    ["On My Way created", data.productUsage.onMyWayCreated],
    ["On My Way arrived", data.productUsage.onMyWayArrived],
    ["Invite conversions", data.productUsage.inviteConversions],
  ];

  return (
    <div className="overview-grid">
      <div className="overview-kpis">
        <button className="admin-kpi" onClick={() => onNavigate("venues")}>
          <span className="admin-kpi-icon"><Building2 size={18} /></span>
          <span className="admin-kpi-label">Premium venues</span>
          <strong>{number.format(data.venues.premium)}</strong>
          <small>{number.format(data.venues.active)} active venues</small>
        </button>
        <button className="admin-kpi" onClick={() => onNavigate("sensors")}>
          <span className="admin-kpi-icon"><Wifi size={18} /></span>
          <span className="admin-kpi-label">Sensors online</span>
          <strong>{number.format(data.sensors.online)} <em>/ {number.format(data.sensors.total)}</em></strong>
          <small>{number.format(data.sensors.offline)} offline</small>
        </button>
        <button className="admin-kpi" onClick={() => onNavigate("users")}>
          <span className="admin-kpi-icon"><Users size={18} /></span>
          <span className="admin-kpi-label">Total users</span>
          <strong>{number.format(data.users.total)}</strong>
          <small>+{number.format(data.users.newLast7Days)} in the last 7 days</small>
        </button>
        <button className="admin-kpi" onClick={() => onNavigate("recognition")}>
          <span className="admin-kpi-icon"><Radio size={18} /></span>
          <span className="admin-kpi-label">Recognitions today</span>
          <strong>{number.format(data.recognition.today.handled)}</strong>
          <small>{number.format(data.recognition.today.auddCalls)} AudD calls</small>
        </button>
      </div>

      {error && <div className="admin-error">Refresh failed: {error}</div>}

      <section className="admin-surface overview-attention">
        <div className="admin-section-heading">
          <div>
            <span className="admin-eyebrow">Operations</span>
            <h2>Needs attention</h2>
          </div>
          <button className="admin-icon-button" type="button" onClick={load} disabled={loading} aria-label="Refresh overview">
            <RefreshCw size={16} className={loading ? "is-spinning" : ""} />
          </button>
        </div>
        {attention.length === 0 ? (
          <div className="overview-all-clear">
            <span>All clear</span>
            <p>No operational issues are currently surfaced by the dashboard.</p>
          </div>
        ) : (
          <div className="attention-list">
            {attention.map((item) => (
              <button key={item.label} type="button" onClick={() => onNavigate(item.section, item.target)}>
                <span className="attention-count">{number.format(item.count)}</span>
                <span className="attention-copy"><strong>{item.label}</strong><small>{item.detail}</small></span>
                <ArrowRight size={16} />
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="admin-surface overview-actions">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Shortcuts</span><h2>Quick actions</h2></div>
        </div>
        <div className="quick-action-grid">
          <button onClick={() => onNavigate("venues", "add-venue")}><Building2 size={18} /><span>Add venue</span></button>
          <button onClick={() => onNavigate("sensors", "setup-code")}><Cpu size={18} /><span>Generate setup code</span></button>
          <button onClick={() => onNavigate("imports")}><RefreshCw size={18} /><span>Import venues</span></button>
          <button onClick={() => onNavigate("users", "create-contributor")}><UserPlus size={18} /><span>Create contributor</span></button>
          <button onClick={() => onNavigate("firmware")}><Upload size={18} /><span>Upload firmware</span></button>
        </div>
      </section>

      <section className="admin-surface overview-usage">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Last 7 days</span><h2>Product usage</h2></div>
        </div>
        <div className="usage-grid">
          {usage.map(([label, value]) => (
            <div key={label}><strong>{number.format(Number(value))}</strong><span>{label}</span></div>
          ))}
        </div>
      </section>

      <section className="admin-surface overview-recent">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Latest jobs</span><h2>Recent activity</h2></div>
          <button type="button" onClick={() => onNavigate("imports")}>View imports</button>
        </div>
        {data.imports.recent.length === 0 ? (
          <p className="admin-empty">No import activity yet.</p>
        ) : (
          <div className="recent-list">
            {data.imports.recent.map((job) => (
              <div key={job.id}>
                <span className={`admin-status admin-status--${job.status}`}>{job.status}</span>
                <span><strong>OSM venue import</strong><small>{job.created} created · {job.updated} updated · {job.skipped} skipped</small></span>
                <time>{new Date(job.createdAt).toLocaleString()}</time>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
